import React, {
    useEffect, useContext, useMemo, useCallback, useState
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { TranslatorContext } from '@jutro/locale';
import { useValidation } from 'gw-portals-validation-react';
import SgTypeAheadComponent from 'sg-typeahead-component';
import { JUTRO_TOPICS, publish } from '@jutro/events';
import metadata from './SgLookup.metadata.json5';

function SgLookup(props) {
    const {
        id,
        modelVM,
        updateData,
        lookupModel,
        lookupValue,
        lookupPath,
        lookupLabel,
        lookupSecondaryLabel,
        lookupTopDropdownText,
        lookupBottomDropdownText,
        lookupResultNotFoundMessage,
        lookupErrorMessage,
        lookupPlaceholderMessage,
        showErrors,
        resetRawInput,
        onValidate: onValidateParent,
        adobeID,
        className
    } = props;
    const {
        isComponentValid,
        onValidate
    } = useValidation(id);

    const translator = useContext(TranslatorContext);
    const [resultFound, setResultFound] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);
    const getName = useCallback((lookup) => {
        return translator({
            id: lookup.name,
            defaultMessage: ''
        });
    }, [translator]);

    const lookupTypeSelectAvailableValues = useMemo(() => {
        return lookupModel.aspects.availableValues
            .map((lookupType) => {
                return {
                    ...lookupType,
                    name: getName(lookupType)
                };
            });
    }, [getName, lookupModel.aspects.availableValues]);

    useEffect(() => {
        return () => {
            if (onValidateParent) {
                // workaround when component was not valid but is no longer needed (hidden)
                onValidateParent(true, id);
            }
        };
    }, []);

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidateParent]);

    const setLookupTypeSelect = useCallback((value, path) => {
        const newModelVM = _.clone(modelVM);
        _.set(newModelVM, path, value);
        updateData(newModelVM);
        publish(JUTRO_TOPICS.VALUE_CHANGED, { adobeID, value });
    }, [modelVM, updateData, adobeID]);

    const onLookupTypeSelectChange = useCallback((value, path) => {
        setErrorMessage(false);
        setResultFound(false);
        setLookupTypeSelect(value, path);
    }, [setLookupTypeSelect]);

    const onLookupTypeSelectBlur = useCallback((value, path) => {
        const matchingLookup = _.find(lookupTypeSelectAvailableValues, ((lookup) => lookup.name.toLowerCase() === value.toLowerCase()));
        if (!_.isEmpty(value) && value.length >= 3) {
            if (!_.isNil(matchingLookup)) {
                setLookupTypeSelect(matchingLookup, path);
                setErrorMessage(false);
            } else {
                setResultFound(true);
                setLookupTypeSelect(value, path);
            }
        } else if (!_.isEmpty(value) && value.length >= 1) {
            if (_.isNil(matchingLookup)) {
                setErrorMessage(true);
                setLookupTypeSelect(value, path);
            }
        } else {
            setLookupTypeSelect(undefined, path);
            setErrorMessage(false);
        }
    }, [setLookupTypeSelect, lookupTypeSelectAvailableValues]);

    const overrideProps = {
        lookupTypeSelect: {
            label: lookupLabel,
            secondaryLabel: lookupSecondaryLabel,
            path: lookupPath,
            value: getName(lookupValue),
            items: lookupTypeSelectAvailableValues,
            searchBy: 'name',
            renderAs: 'name',
            topDropdownText: lookupTopDropdownText,
            bottomDropdownText: lookupBottomDropdownText,
            defaultValueMessage: lookupResultNotFoundMessage,
            noResultsFound: resultFound,
            minLetters: 3,
            onValueChange: onLookupTypeSelectChange,
            handleOnBlur: onLookupTypeSelectBlur,
            showErrorsOverride: showErrors,
            validationMessages: errorMessage ? [lookupErrorMessage] : lookupModel.aspects.validationMessages,
            placeholder: lookupPlaceholderMessage,
            resetRawInput,
            adobeID
        }
    };

    const resolvers = {
        resolveComponentMap: {
            SgTypeAheadComponent: SgTypeAheadComponent
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={modelVM}
            onModelChange={updateData}
            onValidationChange={onValidate}
            overrideProps={overrideProps}
            componentMap={resolvers.resolveComponentMap}
            className={className}
        />
    );
}

SgLookup.propTypes = {
    id: PropTypes.string,
    modelVM: PropTypes.shape({}).isRequired,
    updateData: PropTypes.func.isRequired,
    lookupPath: PropTypes.string.isRequired
};

SgLookup.defaultProps = {
    id: undefined
};

export default SgLookup;
